/**
 * Shared contract for the unified experiment registry (Insights v1).
 * Backend source of truth: builder-service ExperimentRegistryService.
 */

import type { CliCandidateRecord, ResearchReportCardPayload } from '@modules/shared-types';
import type { ReportScopeIdentity } from './types';
import {
  REQUIRED_FRICTION_OUTCOMES,
  type FrictionClass,
  type FrictionOutcomeMetricKey,
} from './creativeDecisions/ethicalDecisioning';

export type { CliCandidateRecord } from '@modules/shared-types';
export type { ResearchReport, ResearchReportCardPayload, ResearchSection } from '@modules/shared-types';
export type {
  PanelPersonaSummary,
  PanelRejectedProposal,
  PanelScientistVerdict,
  PanelSummary,
} from '@modules/shared-types';
export { REQUIRED_FRICTION_OUTCOMES };
export type { FrictionClass, FrictionOutcomeMetricKey };

/** UI options for the exact CFV-AC-053 taxonomy. The type comes from the
 * shared ethical-decisioning policy so additions cannot drift silently. */
export const FRICTION_CLASSES = [
  'accidental',
  'trust',
  'qualification',
  'protective',
  'dark_sludge',
] as const satisfies ReadonlyArray<FrictionClass>;

export type ExperimentType = 'web_ab' | 'pricing' | 'discovery' | 'emulation' | 'analyst' | 'research';

/** Spec E E2b — FE mirror of the analyst review payload (builder reviewReportTypes). */
export type AnalystReviewMode = 'release' | 'decision-brief' | 'anomaly-watch' | 'campaign-brief';

/**
 * D: FE mirror of the campaign-brief grounding (render-only — verdicts and
 * readings are deterministic server rows; the card quotes, never computes).
 */
export interface AnalystCampaignContextSummary {
  window: { from: string; to: string };
  thresholds: { killBelowP90Roas: number; scaleAboveP10Roas: number; minCohortAgeDays: number };
  ltvBasis: string;
  unmatchedSpend: number | null;
  /**
   * AC-H-8 typed state of the unmatched-spend total (additive — absent on
   * pre-wave cards): 'mixed_currency' means the total excludes withheld
   * campaigns and must not render as a complete dollar figure.
   */
  unmatchedSpendMoneyState?: string | null;
  /** Shared currency of the campaign money cells; null = mixed. */
  moneyCurrency?: string | null;
  reportId: string | null;
  economics: Array<{
    rowKey: string;
    campaign: string;
    metaCampaignId: string | null;
    spend: number | null;
    cac: number | null;
    pltv12Roas: number | null;
    pltv12RoasP10: number | null;
    pltv12RoasP90: number | null;
    paybackMonth: number | null;
    paybackBeyondHorizon: boolean;
    cohortAgeDays: number | null;
    lowConfidence: boolean;
    verdict: 'kill' | 'watch' | 'scale' | 'too_young' | 'none';
  } & Record<string, unknown>>;
  contrast: Array<{ rowKey: string; metaCampaignId: string | null; reading: string } & Record<string, unknown>>;
  creatives: Array<Record<string, unknown>>;
}

export interface AnalystFindingFeedback {
  verdict: 'accepted' | 'dismissed';
  by: string;
  at: string;
  note?: string;
}

export interface AnalystFinding {
  title: string;
  detail: string;
  severity: 'info' | 'warning' | 'critical';
  dedupKey: string;
  citations: Array<{ kind: 'metric' | 'experiment' | 'analysis' | 'version'; ref: string }>;
  duplicateOfPrior?: boolean;
  feedback?: AnalystFindingFeedback;
}

export interface AnalystVital {
  value: number | null;
  n: number;
  qualification?: { tier: 'solid' | 'directional' | 'insufficient' };
}

/**
 * Loose FE mirror of the analyst card payload — render-only (frontends never
 * compute statistics; server numbers arrive tier-qualified).
 */
export interface AnalystReviewSummary {
  reviewedAt: string;
  mode?: AnalystReviewMode;
  scope: { onboardingId?: string; versionId?: string; experimentId?: string; from?: string; to?: string };
  model: string;
  vitals?: {
    entrants: number;
    startedJourneys?: number;
    sufficiency?: string;
    v1FirstStepConversion?: AnalystVital;
    v2PaywallReach?: AnalystVital;
    v3OverallPurchase?: AnalystVital;
    v4PaywallConversion?: AnalystVital;
    decomposition?: { consistent: boolean | null };
  } & Record<string, unknown>;
  decisionContext?: { experimentId: string; experimentName: string } & Record<string, unknown>;
  /** D: campaign-brief grounding — economics rows + verdicts (render-only). */
  campaignContext?: AnalystCampaignContextSummary;
  headline: string;
  narrative: string;
  findings: AnalystFinding[];
  topActions: Array<{ action: string; expectedImpact: string; basedOn: string }>;
  truncated?: boolean;
  stopReason: string;
  iterations: number;
  /** E3: W attempts refused by the L3 gate (Proactive fixes OFF). */
  wGate?: {
    blocked: true;
    attempts: Array<{
      tool: string;
      summary: string;
      /** W3 (J6) additive lever context — absent on pre-Wave-3 reports. */
      code?: string;
      lever?: string;
      currentTier?: string;
      requiredTier?: string;
    }>;
  };
  /** E3: W actions the agent performed (Proactive fixes ON). */
  actionsTaken?: Array<{
    type: 'candidate_prepared' | 'draft_created';
    experimentId: string;
    dedupKey?: string;
  }>;
}

/**
 * Run lifecycle for type-`emulation` experiments (Model Emulation Lab,
 * impl-plan §3). Intentionally SEPARATE from the registry `state` machine:
 * the experiment envelope keeps the shared 6-state lifecycle, while the run
 * progresses through these statuses inside it.
 */
export type EmulationRunStatus =
  | 'created'
  | 'capturing'
  | 'generating'
  | 'materializing'
  | 'collecting_evidence'
  | 'resolving'
  | 'proposing'
  | 'completed'
  | 'blocked'
  | 'error';

export type EmulationExperimentLevel =
  | 'L1_strategy_block_graph'
  | 'L2_block_implementation'
  | 'L3_screen_artifact'
  | 'unknown';

export interface EmulationBaseStateRef {
  insightsAnalysisId?: string;
  insightVariableGenerationId?: string;
  strategyId?: string;
}

export interface EmulationSubstrateRef {
  funnelId?: string;
  versionId?: string;
  placementId?: string;
  alias?: string;
  url?: string;
}

export interface EmulationMeasurementWindow {
  start?: string;
  end?: string;
}

/**
 * EmulationRun fields carried FLAT on the experiment document when
 * type === 'emulation' (one-document decision per D47; conceptual shape from
 * impl-plan §3). Populated by the simulator runbook; absent on other types.
 */
export interface EmulationRunFields {
  runSchemaVersion: number;
  /** Equals the experiment document id. */
  runId: string;
  environment: string;
  status: EmulationRunStatus;
  runType?: string;
  experimentLevel: EmulationExperimentLevel;
  baseStateRef?: EmulationBaseStateRef;
  substrate?: EmulationSubstrateRef;
  measurementWindow?: EmulationMeasurementWindow;
  /** Deterministic run identity — warehouse JOIN key (e.g. sim_s1_<cohort>). */
  runTag?: string;
  trafficFilters?: { utmCampaign?: string; pidPrefix?: string };
  /** Sanitized command log — never contains secrets. */
  cliCommandTrace?: string[];
  debugBundlePath?: string;
  gateSummary?: Record<string, unknown>;
  proposalSummary?: string;
  /** Lineage: the S(n-1) run this run iterates on. */
  parentRunId?: string;
  parentSubstrateRef?: string;
}

export type ExperimentState =
  | 'draft'
  | 'running'
  | 'ready_to_decide'
  | 'decided'
  | 'stopped'
  | 'archived';

export type ExperimentAction =
  | 'run'
  | 'mark_ready'
  | 'stop'
  | 'declare_winner'
  | 'archive';

export interface ExperimentHypothesis {
  statement: string;
  primaryMetric: string;
  /** Expected direction/lift, e.g. "+10% purchase conversion". */
  expectedEffect?: string;
  minimumDetectableEffect?: number;
  /** Pre-registered minimum assigned users PER VARIANT (Spec D peeking rule: `solid` requires reaching it). */
  minimumSampleSize?: number;
  successCriteria: string;
  guardrails?: string[];
}

export interface ExperimentSegmentSplit {
  traitKey: string;
  values: string[];
  /** Optional saved cohort backing this split. */
  cohortId?: string;
}

export interface ExperimentDecision {
  verdict: 'ship' | 'dont_ship';
  winnerVariantId?: string;
  rationale: string;
  decidedBy: string;
  decidedAt: string;
}

export interface ExperimentAbTestRef {
  placementId: string;
  abTestId: string;
  /** Allocation epoch pinned to this registry card. Absent on legacy cards. */
  iterationId?: string;
}

export interface CreativeDecisionExperimentHandoff {
  schemaVersion: 1;
  /** Assigned canonically by the server when the draft is persisted. */
  handoffId?: string;
  sourceKind: 'creative_decision';
  experimentType: 'web_ab';
  handoffIntent: 'scale_probe' | 'next_read';
  sourceFrom: string;
  sourceTo: string;
  deploymentKey: string;
  projectionId: string;
  projectionVersion: string;
  scopeRef: string;
  provider: 'meta' | 'google_ads' | 'tiktok_ads';
  providerAccountId: string;
  providerResourceName: string;
  capabilitySnapshotId: string;
  sourceRevisionRefs: string[];
  artifactSnapshotKeys?: string[];
  decision: 'observe' | 'hold' | 'investigate' | 'controlled_scale' | 'refresh_due' | 'stop_candidate';
  primaryAction: string;
  decisionMetricKeys: string[];
  evidenceRefs: string[];
  fixedFactors: string[];
  factorsToVary: string[];
  targetEstimand: string;
  primaryMetric: string;
  guardrails: string[];
  minimumDetectableEffect: number;
  minimumDurationDays: number;
  stoppingPolicy: string;
  surrogate: {
    status: 'available' | 'unavailable';
    reasonCodes: string[];
    evidenceRefs: string[];
    metricDefinitionRef?: string;
  };
  sourceAsOf: string;
}

/**
 * Pricing pre-registration payload (pricing plan Increment 3, AC-009).
 * FE mirror of the builder's PricingExperimentSpec — keep both in sync.
 */
export interface PricingExperimentSpec {
  offers: Array<{
    offerId: string;
    productId: string;
    priceId?: string;
    paywallScreenIds: string[];
  }>;
  treatmentAxis: string;
  /** Must include 'plan_mix' (mandatory cannibalization metric). */
  secondaryMetrics: string[];
  delayedGuardrails: string[];
  /** Pricing-loop provenance (AC-022): the generated candidate id. */
  candidateId?: string;
  /** Pricing-loop provenance (AC-022): kernel input hash at generation. */
  inputHash?: string;
}

export interface ExperimentDocument extends Partial<EmulationRunFields> {
  id: string;
  type: ExperimentType;
  name: string;
  hypothesis: ExperimentHypothesis;
  state: ExperimentState;
  abTestRef?: ExperimentAbTestRef;
  /** Only present when type === 'pricing' (Increment 3 gate payload). */
  pricing?: PricingExperimentSpec;
  creativeDecisionHandoff?: CreativeDecisionExperimentHandoff;
  /** Optional friction classification plus the immutable, complete long-term
   * outcome set that prevents step-conversion-only decisions. */
  frictionClass?: FrictionClass;
  frictionOutcomeMetricKeys?: FrictionOutcomeMetricKey[];
  /** Pre-registered confirmatory segment splits (discovery experiments). */
  segmentSplits?: ExperimentSegmentSplit[];
  decision?: ExperimentDecision;
  /** Spec E: present when type === 'analyst' (1 card = 1 review). */
  analystReview?: AnalystReviewSummary;
  /** E2c Phase B: present when type === 'research' (1 card = 1 report; id = reportId). */
  researchReport?: ResearchReportCardPayload;
  /** Spec D4 (§5.1): candidate conveyor record — the card polls it while preparing. */
  candidate?: CliCandidateRecord;
  /** Pricing loop §2 (AC-080): canonical candidate key persisted on the draft —
   * read by the recommendations memory loop («draft registered», suppression). */
  candidateKey?: string;
  startedAt?: string;
  stoppedAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /**
   * QW1 identity layer: display names resolved server-side on the read path
   * (abTest/placement names for web_ab, substrate names for emulation).
   * Wire-only — never stored on the registry document.
   */
  identity?: ReportScopeIdentity;
  /**
   * Iteration D (AC-1105): audience/actor/insight names for the ids the
   * bounded proposal feed references (B2C). Wire-only, same contract as
   * `identity` — absent on pre-D runs and when resolution finds nothing.
   */
  feedIdentity?: ExperimentFeedIdentity;
}

/** FE mirror of the builder's ExperimentFeedIdentityResolver output. */
export interface ExperimentFeedIdentity {
  audiences: Record<string, { name: string }>;
  actors: Record<string, { name: string; audienceId?: string }>;
  insights: Record<string, { category?: string; description?: string }>;
  analyses: Record<string, { name: string }>;
  loadWarnings?: string[];
}

export interface ExperimentAuditEntry {
  id: string;
  at: string;
  actorUid: string;
  action: string;
  fromState?: ExperimentState;
  toState?: ExperimentState;
  details?: string;
}

export interface ExperimentInputPayload {
  type: ExperimentType;
  name: string;
  hypothesis: Partial<ExperimentHypothesis>;
  abTestRef?: ExperimentAbTestRef;
  creativeDecisionHandoff?: CreativeDecisionExperimentHandoff;
  /** `null` explicitly clears an existing draft registration. A persisted
   * experiment either has an exact FrictionClass or omits the field. */
  frictionClass?: FrictionClass | null;
  frictionOutcomeMetricKeys?: FrictionOutcomeMetricKey[];
  segmentSplits?: ExperimentSegmentSplit[];
  /** Only valid when type === 'pricing'. */
  pricing?: PricingExperimentSpec;
  /** Only valid when type === 'emulation'. */
  emulationRun?: Partial<EmulationRunFields>;
  /** Pricing loop §2 (AC-080): canonical candidate key ({axis, scope, offer
   * set, grain} slug) stored on the draft — the memory-loop dedup identity. */
  candidateKey?: string;
}

export interface ExperimentTransitionPayload {
  action: ExperimentAction;
  /** Required for declare_winner. */
  winnerVariantId?: string;
  rationale?: string;
}

export interface SegmentResponseCell {
  segmentLabel: string;
  traitKey: string;
  values: string[];
  variantId: string;
  preRegistered: boolean;
  metric: string;
  numerator: number;
  denominator: number;
  rate: number | null;
  suppressed: boolean;
}

export interface SegmentResponseMapData {
  experimentId: string;
  metric: string;
  cells: SegmentResponseCell[];
}
